import { coreSync } from './core.js';
import { documentFromRows, documentToRows } from './documents.js';

// ── The rules below are the core's ──────────────────────────────────────────
//
// Every function in this file is an adapter over `core/src/events.rs`, so the
// Android and iOS drawers answer from the same implementation: the form's
// coercion into the core's `EventForm`, and the core's rows turned back into
// the objects these functions return. `core/src/calendar_vectors.json` holds
// the recorded answers, and `test/core/calendar.wasm.test.js` replays them
// through these adapters.
//
// Pure, so it can be checked without a browser, and so it runs unchanged in a
// Cloud Function.

// A temporary weekly hold is MMS's "holds this weekly slot for up to 7 days
// while they confirm" — a recurring series that expires on its own.
//
// It is a studio setting — MMS has it under
// Policies → Booking Policy as "Weekly time slot hold duration ... how many days
// a student's weekly time slot will be reserved after their original event has
// passed" — and the default lives with the other settings defaults, in
// domain/studio.js as `DEFAULT_SCHEDULING.weeklyHoldDays`.
//
// This module does not import it: the value arrives as an argument, so a
// Cloud Function or a test with no studio record still produces a sane series,
// and this is the fallback for a caller that has none — like `currency =
// 'USD'` below. A screen always passes the studio's setting.
const FALLBACK_WEEKLY_HOLD_DAYS = 7;

// Where a series stops, for every one of the three recurrence modes.
//
// Its own function because two callers derive it — this module, from the
// drawer's form, and `createSeries` in data/useEvents.jsx — and they must agree
// on how long a hold is.
//
// Every mode is expressed as a date the series stops on, which is how MMS models
// it: a one-time event ends the day it starts, a temporary hold `weeklyHoldDays`
// later, and a recurring event either repeats indefinitely — null, which the
// expansion treats as unbounded — or until the date the teacher picked.
export function seriesEndDate(input, weeklyHoldDays = FALLBACK_WEEKLY_HOLD_DAYS) {
  return coreSync().seriesEndDate(
    str(input?.recurrenceMode), str(input?.startDate), str(input?.endDate),
    Boolean(input?.repeatIndefinitely), Number(weeklyHoldDays),
  ) ?? null;
}

// ── The adapters' coercion ──────────────────────────────────────────────────
//
// A form's loose values into the core's `EventForm`: text as `String(v)`, which
// the core reads back with JavaScript's own `Number()` where it wants a number,
// flags as `Boolean(v)`, and `null` for nothing. Declared as functions so the
// hoisting reaches `seriesEndDate` above.
function str(v) {
  return v == null ? null : String(v);
}

function flag(v) {
  return v == null ? null : Boolean(v);
}

// `maxStudents` is the Maximum Students box's text (#766), held as typed like
// the duration and interval boxes, and judged by the core.
const FORM_TEXT = [
  'visibility', 'categoryId', 'categoryLabel', 'locationId', 'startDate', 'startTime',
  'durationMin', 'recurrenceMode', 'frequency', 'interval', 'monthlyAnchor', 'endDate',
  'pricingRule', 'priceOverride', 'publicDescription', 'privateDescription', 'maxStudents',
];
const FORM_FLAGS = [
  'requiresMakeupCredit', 'allowOnlineBooking', 'openSlot', 'allDay', 'repeatIndefinitely',
  'showPublicDescription',
];

/// The core's `EventForm`, from the drawer's form.
export function eventFormInput(form = {}) {
  const out = {};
  for (const k of FORM_TEXT) out[k] = str(form?.[k]);
  for (const k of FORM_FLAGS) out[k] = flag(form?.[k]);
  out.studentIds = Array.isArray(form?.studentIds) ? form.studentIds.map(String) : null;
  return out;
}

/// The drawer's form, from the core's: the duration and interval back to the
/// numbers the web's form holds, and a field the core left empty as
/// `null` — except the date, which stays absent, as an input's `value` must.
function formFromCore(f) {
  const out = {};
  for (const [k, v] of Object.entries(f)) out[k] = v === undefined ? null : v;
  for (const k of ['durationMin', 'interval']) {
    if (typeof out[k] === 'string') out[k] = Number(out[k]);
  }
  if (out.startDate == null) out.startDate = f.startDate;
  return out;
}

const SEED_TEXT = [
  'occurrenceId', 'seriesId', 'occurrenceDate', 'startDate', 'seriesStartDate', 'visibility',
  'categoryId', 'categoryLabel', 'locationId', 'startTime', 'recurrenceMode', 'frequency',
  'endDate', 'pricingRule', 'publicDescription', 'privateDescription',
];
const SEED_NUMBERS = ['durationMin', 'interval', 'byWeekday', 'weekOfMonth', 'priceOverrideMinor', 'maxStudents'];
const SEED_FLAGS = ['requiresMakeupCredit', 'allowOnlineBooking', 'allDay', 'showPublicDescription'];

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

/// The core's `EventSeed`, from an `editing` object (`editingFromOccurrence`'s).
function seedInput(editing) {
  if (!editing) return null;
  const out = {};
  for (const k of SEED_TEXT) out[k] = str(editing[k]);
  for (const k of SEED_NUMBERS) out[k] = numOrNull(editing[k]);
  for (const k of SEED_FLAGS) out[k] = flag(editing[k]);
  out.studentIds = Array.isArray(editing.studentIds) ? editing.studentIds.map(String) : null;
  return out;
}

const intOrNull = (v) => {
  const n = numOrNull(v);
  return Number.isInteger(n) ? n : null;
};

/// The core's `SeriesRule`, from a stored series: the fields an edit reads.
export function seriesRuleInput(rule) {
  if (!rule) return null;
  return {
    id: String(rule.id ?? ''),
    startDate: str(rule.startDate),
    frequency: str(rule.frequency),
    interval: numOrNull(rule.interval),
    endDate: str(rule.endDate),
    byWeekday: intOrNull(rule.byWeekday),
    weekOfMonth: intOrNull(rule.weekOfMonth),
    recurrenceMode: str(rule.recurrenceMode),
  };
}

const exceptionsOf = (rule) => (Array.isArray(rule?.exceptions)
  ? rule.exceptions.filter((d) => typeof d === 'string')
  : []);

/// The core's `EventOccurrence`, from a calendar occurrence.
export function eventOccurrenceInput(o) {
  if (!o) return null;
  return {
    id: str(o.id),
    seriesId: str(o.seriesId),
    date: str(o.date),
    ruleDate: str(o.ruleDate),
    startTime: str(o.startTime),
    durationMin: numOrNull(o.durationMin),
    studentIds: Array.isArray(o.studentIds) ? o.studentIds.map(String) : null,
    categoryId: str(o.categoryId),
    categoryLabel: str(o.categoryLabel),
    pricingRule: str(o.pricingRule),
    priceOverrideMinor: numOrNull(o.priceOverrideMinor),
    locationId: str(o.locationId),
    visibility: str(o.visibility),
    requiresMakeupCredit: flag(o.requiresMakeupCredit),
    allowOnlineBooking: flag(o.allowOnlineBooking),
    allDay: flag(o.allDay),
    publicDescription: str(o.publicDescription),
    privateDescription: str(o.privateDescription),
    recurrenceMode: str(o.recurrenceMode),
    maxStudents: numOrNull(o.maxStudents),
  };
}

// ─── How long a lesson may be, and why that is a whole number ──────────────
//
// #137. `durationMin` must be a whole number of minutes:
//
//   the input   `<input type="number" min="5" step="5">`, on the event drawer
//               (DetailsSection.jsx) and on the add-student wizard
//               (LessonSettingsStep.jsx). `step` is **advisory** — neither
//               input is inside a form that submits, so typing 37.5 is not
//               refused by the browser.
//   the rule    firestore.rules holds `durationMin` to an int range, so a
//               double is denied as a permission error with no message about
//               duration.
//   the phone   `duration_min` is `Option<i64>` in core/src/schedule.rs,
//               core/src/conflicts.rs and core-ffi/src/lib.rs, and
//               `Schedule.kt` reads it with `.toLong()`, so a stored 37.5
//               would **truncate to 37** there.
//
// A duration is minutes on a wall clock: `spanOf` in domain/conflicts.js adds
// it to an `HH:MM` start, the week grid multiplies it by pixels-per-minute,
// and `liveStatus.js` adds `durationMin * 60_000` to a Date.
//
// So a fraction is refused at the field, with Save held shut, rather than
// rounded (#93; `tagsWithinBounds` in domain/people.js is a predicate rather
// than a clamp for the same reason) — the shape #90 gave an undialable phone
// number and #129 a duplicate category name.
//
// ── The upper bound ────────────────────────────────────────────────────────
//
// `max` is an argument and defaults to none, so a caller with no rule behind
// it does not acquire one. Both callers here pass it, because both fields'
// rules say the same thing:
//
//   `validStudent.defaultDurationMin`  `requiredIntInRange(d, k, 1, 1440)`
//   `validEventDoc.durationMin`        `optionalIntInRange(d, k, 1, 1440)`
//
// The student's is `required` (#352) — MMS refuses an empty Duration — which
// is what the `required` option is for.
//
// 1440 is a day (**#148**, **#149**), because a day is all the calendar can
// draw: an occurrence is painted in the column of its start date, and MMS's
// answer to a longer event is an "All day" checkbox beside this field.
//
// DURATION_MAX_MINUTES is both rules read back out (#121, #148), so the two
// fields and their two rules cannot drift.
export const DURATION_MAX_MINUTES = 1440;

// Why this is what a lesson's duration may not be, or null if it is fine.
//
// **Blank is fine on an event, and not on a student** — one function, two
// callers, and they disagree about the empty box (#352). `required` is the
// switch and it defaults to off, for the event drawer (#137).
//
// On the student's field MMS refuses an empty Duration on both create and edit
// (#352: `Please enter a valid duration`, range 1–1440, wizard pre-filled with
// 30), and a make-up credit is divided by that number. There is no fallback
// thirty.
//
// **A negative is not blank** in either mode: it gets the `< 1` sentence. A
// typed zero goes to the same `< 1` message rather than to "enter a duration",
// as in MMS (`Please enter an amount greater than or equal to 1` at 0, against
// `Please enter a valid duration` at empty).
//
// The wording is ours; *which values are refused* is MMS's.
//
// **The rule is `people::duration_error` in the core**, so the phones'
// Duration boxes refuse the same values with the same sentences
// (`core/src/people_forms_vectors.json`). What is left is the coercion: a
// number crosses as its `String()`, which the core reads back with
// JavaScript's own `Number()` rules, and nothing at all crosses as null.
export function durationError(value, max = null, { required = false } = {}) {
  const raw = typeof value === 'string' ? value : value == null ? null : String(value);
  const refusal = coreSync().durationError(raw, max ?? undefined, Boolean(required));
  return refusal ? refusal.message : null;
}

// Turning the event drawer's form into the fields an eventSeries document
// has. The form carries two things the document must never see —
// `repeatIndefinitely` and `priceOverride` (a decimal string) — and two real
// fields are derived from those:
//
//   endDate is null for "repeat indefinitely", and for the two modes that are
//     not open-ended it is derived rather than typed,
//   priceOverrideMinor is integer minor units, and only when the rule is fixed.
//
// The whole form must not be spread into the patch: updateSeries merges what it
// is given, so *editing* an indefinite series would write a stale bound next
// to repeatIndefinitely: true, and the lesson would stop repeating.
//
// `currency` decides how many decimal places the typed override has. "3000" is
// 3000 minor units in yen and 300000 in dollars, so the studio's currency —
// derived from its locale, see ./locale.js — has to come in with the form. It
// defaults to USD so a test or a Cloud Function without a studio still parses;
// a screen always passes it.
//
// `weeklyHoldDays` is the studio's `scheduling.weeklyHoldDays`, likewise.
//
// What each field carries, which the core's `series_fields` writes:
//
// * `startTime` is wall-clock time in the series' own zone, which is the
//   sibling field `timeZone` and nothing else — see domain/timeZone.js for why
//   that pair is serialized rather than read from settings/studio at render
//   time. The zone is stamped at creation and never rewritten by this.
// * `allDay` is MMS's "All day" checkbox (#312), a definite `Boolean(...)`
//   rather than `?? null`, as `requiresMakeupCredit` is: a *document* stores a definite flag, and it is the fold that gives
//   absence a meaning. An override written by `occurrenceFieldsFromForm`
//   therefore always says `true` or `false`, which is what lets one entry of an
//   all-day series be an ordinary lesson — `core/src/schedule.rs`'s `mod
//   all_day` has the case.
// * `byWeekday` / `weekOfMonth` are MMS's other monthly anchor, and only ever a
//   monthly one: null on every other frequency, so switching a rule away from
//   monthly and back does not resurrect an old weekday.
// * `priceOverrideMinor`: only a fixed rule carries a price. Left set, a stale
//   override would be read back the next time the rule changed to fixed.
// * `showPublicDescription`: the series' flag, which the calendar, the Student
//   Portal and the reminder worker read (#796).
export function seriesFieldsFromForm(form, currency = 'USD', weeklyHoldDays = FALLBACK_WEEKLY_HOLD_DAYS) {
  return documentFromRows(coreSync().eventSeriesFields(
    eventFormInput(form), currency ?? null, Number(weeklyHoldDays),
  ));
}

// The subset that makes sense on a single occurrence.
//
// `startDate` is absent, because an occurrence has no start date of its own: which date it falls on comes from expanding the rule, and its
// identity — occ_<series>__<rule date> — is built from that. A moved lesson is
// therefore not a changed date but a *displacement*: `movedDate` says where it
// is actually taught, while the rule date it is keyed by stays put. Every
// derived record — attendance, charge, payment — keeps hanging off the same id.
//
// `allDay` is in it: one entry of a series can be all-day on its own — "no
// lesson on the 14th, the studio is shut" — and one entry of an all-day series
// can be an ordinary lesson. The fold reads an override that is *silent* as
// inheriting, so the flag has to be written on every override this makes rather
// than only on the ones that turn it on. The list is `OCCURRENCE_FIELDS` in
// `core/src/events.rs`.
//
// `ruleDate` is the date the rule puts this occurrence on — not the date the
// form is showing, which is where it has been moved to. Passing it lets moving a
// lesson back to its rule date clear the displacement rather than pin the
// occurrence with an override that says nothing.
export function occurrenceFieldsFromForm(form, ruleDate = null, currency = 'USD') {
  return documentFromRows(coreSync().occurrenceOverrideFields(
    eventFormInput(form), str(ruleDate), currency ?? null,
  ));
}

// What the edit drawer needs to open on one occurrence of a series.
//
// Three dates matter here and only two of them are the same thing:
//
//   startDate      where the lesson is taught — what the Date field shows, and
//                  editing it is how a lesson gets moved
//   occurrenceDate the date the rule puts it on, which is its identity: which
//                  override the edit writes, and where a split cuts
//   seriesStartDate where the rule itself begins, so a split at the first
//                  occurrence is recognised as an edit of the whole series
//
// The recurrence settings come from the rule rather than the occurrence,
// because repeating is a property of the series and no single instance of it —
// the end date included: a split carries the rule's bound onto the new rule,
// so "move this and everything after it" keeps the term's end. So is
// `showPublicDescription`, which only the series carries.
//
// The occurrence's own fields stay on the object as well; the core answers
// the ones the form and Save read.
export function editingFromOccurrence(occurrence, rule = {}) {
  const stored = rule && Object.keys(rule).length ? rule : null;
  const seed = coreSync().eventSeed(
    eventOccurrenceInput(occurrence),
    seriesRuleInput(stored),
    stored?.showPublicDescription == null ? null : Boolean(stored.showPublicDescription),
  );
  const out = { ...occurrence };
  for (const [k, v] of Object.entries(seed)) if (v !== undefined) out[k] = v;
  // The rule's three, null when absent.
  for (const k of ['byWeekday', 'weekOfMonth', 'endDate']) out[k] = seed[k] ?? null;
  out.seriesStartDate = seed.seriesStartDate;
  return out;
}

// ─── The rest of the event drawer's rules ──────────────────────────────────
//
// The core's rules for calendar editing, called from `src/hooks/useEventForm.js`,
// `src/data/useEvents.jsx` and `src/pages/CalendarPage.jsx`.
//
// Nothing here writes. A document is built with the sentinels a writer holds
// — `serverTimestamp`, `arrayUnion` — handed in, because this directory may
// not import Firebase.

// Categories as the core reads them: an id and a label, strings or nothing.
export function categoryRefs(categories = []) {
  return (categories || []).map((c) => ({
    id: c?.id == null ? null : String(c.id),
    label: typeof c?.label === 'string' ? c.label : null,
  }));
}

// The form a drawer opens with (`initialForm` and `categorySeed` in
// useEventForm.js). `categories` is the studio's list, for a new event's seed.
//
// The category pair: an edit takes the event's own, whatever they are —
// including a bare label with no id, which is #110's shape and is the
// teacher's to resolve rather than ours to overwrite. A new event takes the
// studio's Lesson, as a real category rather than a bare label (#128): the id
// is what a rename, a recolour and a reminder lead reach (#98, #110). Seeded
// here rather than in an effect so the first paint is already right; the
// hook's effect is the repair for a list that arrives afterwards.
//
// `openSlot` is its own fact, not something to infer from an empty attendee
// list: "a lesson whose student I have not picked yet" and "a slot
// deliberately held open" look identical in the data. The duration is not
// seeded from the attendee here — at mount a new event has none; that is
// `seededDuration` (#241). "All day" (#160) is a flag, not a duration, and the
// form keeps the time and duration while it is ticked, as MMS does. A stored
// `biweekly` opens as Weekly / 2 through `formRecurrence` (#26). Only a
// recurring series reads its end back as "repeats until", and a form with no
// pricing section starts at no charge, since nothing may be charged for an
// event the teacher was never shown a price for.
export function initialEventForm({
  editing, initialDate, initialTime, initialDuration, shape, currency, categories = [],
} = {}) {
  return formFromCore(coreSync().initialEventForm(
    seedInput(editing),
    str(initialDate),
    str(initialTime),
    numOrNull(initialDuration),
    shape ? Boolean(shape.pricing) : null,
    currency ?? null,
    categoryRefs(categories),
  ));
}

// The form after the teacher picks a category (`chooseCategory` in the hook,
// #128): keyed on the id, both halves of the pair, and no charge for a
// category marked not billable. Null for an id that names nothing, which
// changes nothing.
export function chooseEventCategory(form, id, categories = []) {
  if (!(categories || []).some((c) => c?.id === id)) return null;
  const next = coreSync().chooseEventCategory(
    eventFormInput(form),
    String(id),
    categories.filter((c) => c?.id != null).map((c) => ({
      id: String(c.id), label: String(c.label ?? ''), billable: c.billable === false ? false : null,
    })),
  );
  return {
    ...form,
    categoryId: next.categoryId ?? null,
    categoryLabel: next.categoryLabel ?? '',
    pricingRule: next.pricingRule ?? form.pricingRule,
  };
}

// The duration a new lesson takes from the students attending it (#241), or
// null where there is none to take — see the effect in useEventForm.js for
// its three rules and which of them are ours. `students` is the provider's
// list, each row carrying `durationMin`; a duration that is not a number is
// none.
export function seededDuration(studentIds = [], students = []) {
  return coreSync().seededDuration(
    (studentIds || []).map(String),
    (students || []).map((st) => ({
      id: String(st?.id ?? ''),
      durationMin: typeof st?.durationMin === 'number' ? st.durationMin : null,
    })),
  ) ?? null;
}

// Whether Save is enabled (`canSave` in useEventForm.js). It also holds Save
// shut on a Repeats Every the rules would deny — while the recurrence mode
// shows the field; a hidden one is written as 1 instead.
export function eventCanSave(form, shape) {
  return coreSync().eventCanSave(eventFormInput(form), Boolean(shape?.requireAttendee));
}

// The fixed price's refusal, judged only under the fixed rule (#171).
export function eventPriceError(form, currency) {
  return coreSync().eventPriceError(eventFormInput(form), currency ?? null)?.message ?? null;
}

// Repeats Every's refusal: a fraction, a negative, or a number too
// large to be finite, which the rules deny; empty, zero or unreadable write 1.
// Only on a recurring event, the one mode that shows the field.
export function eventIntervalError(form) {
  return coreSync().eventIntervalError(eventFormInput(form))?.message ?? null;
}

// Maximum Students' own field message (#766), or null: judged only while
// Allow online booking shows the box, and Save is held shut on it
// (`form_max_students_error` in core/src/events.rs, where MMS's box and our
// 1–30 are written up).
export function eventMaxStudentsError(form, currency = 'USD') {
  return [...coreSync().eventFormErrors(eventFormInput(form), currency ?? null)]
    .find((e) => e.field === 'maxStudents')?.message ?? null;
}

// Whether an edit is one entry of a repeating series, and so asks a scope.
export function isRecurringEdit(editing) {
  return coreSync().isRecurringEdit(seedInput(editing));
}

// Whether Save opens "Update Future Entries" rather than the scope question.
export function recurrenceChanged({ editing, savedRule, form, currency = 'USD', weeklyHoldDays = FALLBACK_WEEKLY_HOLD_DAYS }) {
  return coreSync().recurrenceChanged(
    eventFormInput(form), seedInput(editing), seriesRuleInput(savedRule), exceptionsOf(savedRule),
    currency ?? null, Number(weeklyHoldDays),
  );
}

// The date in the "Repeat Until" warning, or null.
export function deletesEntriesAfter(form, savedRule) {
  return coreSync().deletesEntriesAfter(
    eventFormInput(form), seriesRuleInput(savedRule), exceptionsOf(savedRule),
  ) ?? null;
}

// What Save does. Asked once with no scope — which may answer that a scope has
// to be asked first — and again with the scope the dialog handed back.
//
//   refuse         the fixed price is refused, so nothing is written
//   askScope       open "Update Recurring Entries" (this entry / following)
//   askRecurrence  open "Update Future Entries" (one Update, no radios)
//   create         a new series
//   updateRule     the rule in place, with `startDate` as its start (see
//                  `planRuleUpdate` for what it does to the overrides)
//   override       an override on `editing.occurrenceId`
//   split          end the rule on `endDate`, drop its overrides from `cut`,
//                  and create a new series from the form
//
// `conflict` is what the conflict check proposes: the whole `rule`, or one
// `entry`, with `ignoreRuleDate` excluded from the stored calendar.
export function planEventSave({
  form, editing = null, savedRule = null, scope = null, currency, weeklyHoldDays,
} = {}) {
  const p = coreSync().planEventSave(
    eventFormInput(form), seedInput(editing), seriesRuleInput(savedRule), exceptionsOf(savedRule),
    str(scope), currency ?? null, weeklyHoldDays == null ? undefined : Number(weeklyHoldDays),
  );
  return {
    action: p.action ?? null,
    startDate: p.startDate ?? null,
    cut: p.cut ?? null,
    endDate: p.endDate ?? null,
    conflict: p.conflict ?? null,
    ignoreRuleDate: p.ignoreRuleDate ?? null,
  };
}

// What editing the rule in place does to the per-entry decisions under it
// (`updateRule` in useEventForm.js, where MMS's behaviour is recorded): every
// override and exception goes when the dates move, the tail goes when the run
// got shorter, and otherwise nothing. `exceptions` is null when the edit does
// not rewrite them.
export function planRuleUpdate(before, fields) {
  const p = coreSync().planRuleUpdate(
    seriesRuleInput(before), exceptionsOf(before), documentToRows(fields), null,
  );
  return {
    dropAllOverrides: p.dropAllOverrides,
    dropOverridesFrom: p.dropOverridesFrom ?? null,
    exceptions: p.exceptions ?? null,
  };
}

// ── The documents ───────────────────────────────────────────────────────────

// What `createSeries` in data/useEvents.jsx writes. `timeZone` is resolved by
// the caller — the input's own zone when it carries one, the studio's
// otherwise. It writes `allDay`; without it a new all-day event would be
// stored as an ordinary one.
export function newSeriesDocument(input, { weeklyHoldDays, timeZone = null, serverTimestamp } = {}) {
  return documentFromRows(coreSync().newSeriesDocument(
    documentToRows(input),
    weeklyHoldDays == null ? undefined : Number(weeklyHoldDays),
    str(timeZone),
  ), serverTimestamp);
}

// What `updateSeries` writes: the patch, merged, and `updatedAt`.
export function seriesUpdateDocument(patch, serverTimestamp) {
  return documentFromRows(coreSync().seriesUpdateDocument(documentToRows(patch), null, undefined), serverTimestamp);
}

// What `overrideOccurrence` writes: the two facts the id is built from, the
// patch, and `updatedAt`, merged onto the occurrence's deterministic id.
export function occurrenceOverrideDocument(occId, patch, serverTimestamp) {
  return documentFromRows(
    coreSync().occurrenceOverrideDocument(String(occId), documentToRows(patch)),
    serverTimestamp,
  );
}

// What a lesson moved by itself writes onto its one occurrence: the week
// grid's drag here, and the phones' Reschedule sheet (#482) — the core's
// `reschedule_patch`, so the three cannot disagree. A bare override, only
// what moved (`movedDate`, `startTime`, `durationMin`), empty when nothing
// did; no scope question and no conflict check, as on MMS's drag.
// `editing` is `editingFromOccurrence`'s; the rest are what was dragged or
// typed, each absent for "unchanged".
export function reschedulePatch(editing, { date = null, time = null, duration = null } = {}) {
  return documentFromRows(coreSync().reschedulePatch(seedInput(editing), str(date), str(time), str(duration)));
}

// What `removeOccurrence` writes to the series — the rule date into
// `exceptions` by `arrayUnion`, so two devices removing two entries offline
// both survive — or null for an id that names no occurrence. The override
// document is deleted beside it.
export function occurrenceRemovalDocument(occId, { serverTimestamp, arrayUnion }) {
  const rows = coreSync().occurrenceRemovalDocument(String(occId));
  return rows.length ? documentFromRows(rows, serverTimestamp, { arrayUnion }) : null;
}

// The overrides a cleanup deletes (`dropOverridesFrom`, and with no
// `fromDate`, `dropAllOverrides`): every one keyed to `seriesId`, or only those
// from `fromDate` on. Their ids, in the order the list holds them.
export function overridesToDrop(overrides = [], seriesId, fromDate = null) {
  return coreSync().overridesToDrop(
    (overrides || []).map((o) => ({ id: String(o.id), seriesId: str(o.seriesId), date: str(o.date) })),
    String(seriesId),
    str(fromDate),
  );
}

